
import { Room, Message } from "@/types/chat";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send, ArrowLeft } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CallControls } from "./CallControls";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect, useRef } from "react";

interface ChatViewProps {
  room: Room;
  messages: Message[] | undefined;
  newMessage: string;
  onSendMessage: () => void;
  onMessageChange: (value: string) => void;
  onStartVideoCall: () => void;
  onStartVoiceCall: () => void;
  isInCall: boolean;
  currentUserId?: string;
  onBack?: () => void;
}

export const ChatView = ({
  room,
  messages,
  newMessage,
  onSendMessage,
  onMessageChange,
  onStartVideoCall,
  onStartVoiceCall,
  isInCall,
  currentUserId,
  onBack
}: ChatViewProps) => {
  const isMobile = useIsMobile();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom of messages when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const otherParticipant = room.participants?.find(p => p.user_id !== currentUserId);
  const displayName = otherParticipant?.profiles?.username || room.name || 'Chat';

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] bg-background relative flex-1">
      <div className="p-3 border-b bg-card flex items-center gap-3">
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="lg:hidden"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <Avatar className="h-10 w-10">
          <AvatarImage src={otherParticipant?.profiles?.avatar_url} />
          <AvatarFallback>{displayName[0]?.toUpperCase() || '?'}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <h3 className="text-base font-semibold">{displayName}</h3>
          <p className="text-xs text-muted-foreground">
            {isInCall ? "Active call" : "Online"}
          </p>
        </div>
        <CallControls
          onStartVideoCall={onStartVideoCall}
          onStartVoiceCall={onStartVoiceCall}
          isInCall={isInCall}
        />
      </div>
      
      <ScrollArea className="flex-1 p-4 bg-[#F0F2F5] dark:bg-zinc-900 overflow-y-auto">
        <div className="space-y-4 pb-4">
          {!messages?.length && (
            <div className="text-center py-8 h-full flex flex-col items-center justify-center mt-20">
              <div className="bg-background p-6 rounded-full mb-4">
                <Send className="h-10 w-10 text-muted-foreground rotate-45" />
              </div>
              <h3 className="text-xl font-medium mb-2">Start chatting now</h3>
              <p className="text-muted-foreground max-w-xs">
                Send a message to begin your conversation with {displayName}
              </p>
            </div>
          )}
          {messages?.map((message) => (
            <div
              key={message.id}
              className={`flex gap-2 ${
                message.sender_id === currentUserId ? "justify-end" : "justify-start"
              }`}
            >
              {message.sender_id !== currentUserId && (
                <Avatar className="h-8 w-8">
                  <AvatarImage src={otherParticipant?.profiles?.avatar_url} />
                  <AvatarFallback>
                    {otherParticipant?.profiles?.username?.[0]?.toUpperCase() || '?'}
                  </AvatarFallback>
                </Avatar>
              )}
              <div
                className={`rounded-lg p-3 max-w-[70%] ${
                  message.sender_id === currentUserId
                    ? "bg-flux-cyan text-black rounded-tr-none"
                    : "bg-white dark:bg-zinc-800 rounded-tl-none"
                }`}
              >
                <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                <p className={`text-[10px] text-right mt-1 ${
                  message.sender_id === currentUserId 
                    ? "text-black/70" 
                    : "text-muted-foreground"
                }`}>
                  {new Date(message.created_at).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>
      
      <div className="sticky bottom-0 left-0 right-0 p-3 bg-card border-t">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => onMessageChange(e.target.value)}
            placeholder="Type a message..."
            className="flex-1"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSendMessage();
              }
            }}
          />
          <Button onClick={onSendMessage} size="icon" className="shrink-0">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
